import { Link, useParams } from '@tanstack/react-router'
import { IntlProvider } from 'use-intl'

import { BackgroundPattern } from '@/components/ui/background-pattern'
import { Button } from '@/components/ui/button'
import { ClientAreaButton } from '@/components/cta-buttons'
import { Logo } from '@/components/ui/logo'
import { Reveal } from '@/components/ui/reveal'
import { DEFAULT_LOCALE, isLocale, type Locale } from '@/i18n/config'
import { resolveNotFoundForLocale } from './not-found.helpers'

// `notFoundComponent` on the root route can render before `/$locale`'s
// `IntlProvider` ever mounts (invalid locale segment, or a path with no
// locale segment at all — e.g. a bot hitting a garbage top-level URL).
// `ClientAreaButton` calls `useTranslations()`, which throws without a
// provider ancestor: confirmed via `pnpm build` + a local server hit on
// `/xx` and `/totally-unmatched-top-level` (both 500'd before this fix).
// A minimal local provider — just the one key that button reads — covers
// it without depending on the loader-only `loadMessages`.
// ponytail: duplicates cta.clientArea from messages/{fr,en}.json — this page
// renders outside IntlProvider's normal message load, so it can't reuse
// loadMessages(); keep these two strings in sync if that copy ever changes.
const CLIENT_AREA_LABEL: Record<Locale, string> = {
  fr: 'Espace client',
  en: 'Client area',
}

export function NotFoundPage() {
  // Route param, not `window`: identical on server and client, so no
  // hydration mismatch. `$locale`'s own `beforeLoad` already rejects
  // invalid locales, so a matched `locale` param is always valid here.
  const params = useParams({ strict: false }) as { locale?: string }
  const insideSiteChrome = isLocale(params.locale ?? '')
  const locale = insideSiteChrome ? (params.locale as Locale) : DEFAULT_LOCALE
  const { copy, links } = resolveNotFoundForLocale(locale)

  const content = (
    <Reveal className="mx-auto max-w-xl text-center">
      <p className="font-display text-7xl font-semibold text-primary sm:text-8xl">
        404
      </p>
      <h1 className="mt-4 text-2xl font-semibold sm:text-3xl">{copy.title}</h1>
      <p className="text-muted-foreground mt-3">{copy.subtitle}</p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Button asChild>
          <Link to="/$locale" params={{ locale } as never}>
            {copy.home}
          </Link>
        </Button>
        <IntlProvider
          locale={locale}
          messages={{ cta: { clientArea: CLIENT_AREA_LABEL[locale] } } as never}
          timeZone="Europe/Paris"
        >
          <ClientAreaButton />
        </IntlProvider>
      </div>

      <div className="mt-10">
        <p className="text-muted-foreground text-sm">{copy.quickTitle}</p>
        <ul className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          {links.map((link) => (
            <li key={link.to}>
              <Link
                to={link.to as never}
                params={{ locale } as never}
                className="text-primary text-sm underline-offset-4 hover:underline"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  )

  // Inside `/$locale`, the site's own Header/Footer already wrap this
  // (LocaleLayout) — no second logo header. Mirror the hero: pull the section
  // up under the transparent sticky header with `-mt-(--header-h)` so the
  // gradient runs behind it (no white band above the fold), then restore the
  // gap with matching top padding. `min-h-screen` fills the viewport.
  if (insideSiteChrome) {
    return (
      <div className="relative isolate -mt-(--header-h) flex min-h-screen flex-col items-center justify-center px-6 pt-(--header-h) pb-12">
        <BackgroundPattern />
        {content}
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen flex-col">
      <BackgroundPattern />

      <header className="p-6">
        <Link to="/$locale" params={{ locale } as never} aria-label="CountAct">
          <Logo size="md" alt="CountAct" />
        </Link>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        {content}
      </main>
    </div>
  )
}
